import { useEffect, useRef, useState } from 'react';

const QUOTES = [
  {
    q: "Première inscription sur Kourse en 30 secondes, certificat médical scanné depuis le canapé. Adopté.",
    a: 'Sophie M.',
    r: '34 ans · trail · Annecy',
    c: '#caff00',
  },
  {
    q: "On a basculé notre 10K sur Kourse cette année. 240 dossards, paiements automatiques, équipe sereine.",
    a: 'Julien R.',
    r: 'Organisateur · La Rochelle',
    c: '#00e5ff',
  },
  {
    q: "Enfin une plateforme française pensée pour les coureurs. Filtres clairs, profil unique, fini les comptes en double.",
    a: 'Karim D.',
    r: '28 ans · marathon · Toulouse',
    c: '#ff2d9e',
  },
];

const Star = ({ color = '#caff00', size = 14 }: { color?: string; size?: number }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill={color} aria-hidden>
    <path d="M12 2l3 7h7l-6 4 2 7-6-4-6 4 2-7-6-4h7z" />
  </svg>
);

const TestimonialsSection = () => {
  const carouselRef = useRef<HTMLDivElement>(null);
  const [active, setActive] = useState(0);
  const [isMobile, setIsMobile] = useState(false);
  const [paused, setPaused] = useState(false);

  useEffect(() => {
    const mq = window.matchMedia('(max-width: 768px)');
    const update = () => setIsMobile(mq.matches);
    update();
    mq.addEventListener('change', update);
    return () => mq.removeEventListener('change', update);
  }, []);

  useEffect(() => {
    if (!isMobile) return;
    const el = carouselRef.current;
    if (!el) return;
    const onScroll = () => {
      const w = el.clientWidth;
      if (!w) return;
      const idx = Math.round(el.scrollLeft / w);
      setActive(Math.min(QUOTES.length - 1, Math.max(0, idx)));
    };
    el.addEventListener('scroll', onScroll, { passive: true });
    return () => el.removeEventListener('scroll', onScroll);
  }, [isMobile]);

  useEffect(() => {
    if (!isMobile || paused) return;
    const rm = window.matchMedia('(prefers-reduced-motion: reduce)');
    if (rm.matches) return;
    const el = carouselRef.current;
    if (!el) return;
    const id = setInterval(() => {
      const w = el.clientWidth;
      if (!w) return;
      const cur = Math.round(el.scrollLeft / w);
      const next = cur + 1;
      if (next >= QUOTES.length) {
        el.scrollTo({ left: 0, behavior: 'smooth' });
      } else {
        el.scrollTo({ left: next * w, behavior: 'smooth' });
      }
    }, 4200);
    return () => clearInterval(id);
  }, [isMobile, paused]);

  const scrollTo = (i: number) => {
    const el = carouselRef.current;
    if (!el) return;
    el.scrollTo({ left: i * el.clientWidth, behavior: 'smooth' });
  };

  const pause = () => setPaused(true);
  const resume = () => setPaused(false);

  return (
    <section className="relative border-t border-white/10 py-24 md:py-28 bg-[#080810]">
      <div className="container mx-auto px-4 lg:px-8">
        <div className="text-center mb-14 md:mb-16">
          <div className="font-mono text-[11px] tracking-[0.14em] uppercase mb-4" style={{ color: '#f8ff3a' }}>
            // CHAPITRE QUATRE · VOIX DE LA COMMUNAUTÉ
          </div>
          <h2 className="m-0 leading-[0.95]">
            <span className="font-display italic uppercase -skew-x-6 inline-block text-[44px] md:text-[64px] lg:text-[88px] text-foreground">
              180 000 COUREURS.
            </span>
            <br />
            <span
              className="font-display italic uppercase -skew-x-6 inline-block text-[44px] md:text-[64px] lg:text-[88px] text-transparent bg-clip-text"
              style={{ backgroundImage: 'linear-gradient(90deg, #caff00 0%, #f8ff3a 35%, #ff7a1a 65%, #ff2d9e 100%)' }}
            >
              QUELQUES-UNS EN PARLENT.
            </span>
          </h2>
          <div className="inline-flex items-center gap-3 mt-8 px-5 py-2.5 bg-card/70 border border-white/10 rounded-full">
            <div className="flex gap-0.5">
              {[1, 2, 3, 4, 5].map((i) => <Star key={i} color="#caff00" />)}
            </div>
            <span className="text-sm text-foreground">
              <b>4,8 / 5</b>
            </span>
            <span className="text-xs text-muted-foreground">· 312 avis vérifiés</span>
          </div>
        </div>

        <div
          ref={carouselRef}
          className="k-testi-grid grid grid-cols-1 md:grid-cols-3 gap-6"
          onTouchStart={pause}
          onPointerDown={pause}
          onMouseEnter={pause}
          onMouseLeave={resume}
        >
          {QUOTES.map((q) => (
            <div
              key={q.a}
              className="k-testi-card relative p-7 md:p-8 bg-card/60 border border-white/10 rounded-2xl overflow-hidden"
            >
              <div
                className="font-display italic leading-[0.5] opacity-30"
                style={{ fontSize: 80, color: q.c }}
                aria-hidden
              >
                "
              </div>
              <p className="text-[17px] leading-snug text-foreground font-medium mt-[-18px] mb-6">
                {q.q}
              </p>
              <div className="flex items-center gap-3 pt-5 border-t border-white/10">
                <div
                  className="w-10 h-10 rounded-full grid place-items-center text-sm font-extrabold text-black"
                  style={{ background: q.c }}
                >
                  {q.a.split(' ').map((n) => n[0]).join('')}
                </div>
                <div>
                  <div className="text-[13px] text-foreground font-semibold">{q.a}</div>
                  <div className="text-[11px] text-muted-foreground tracking-wide">{q.r}</div>
                </div>
                <div className="ml-auto flex gap-0.5">
                  {[1, 2, 3, 4, 5].map((j) => <Star key={j} color={q.c} size={10} />)}
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="k-testi-swipe">
          <div className="k-testi-dots" role="tablist" aria-label="Témoignages">
            {QUOTES.map((q, i) => (
              <button
                key={q.a}
                role="tab"
                aria-selected={active === i}
                aria-label={`Témoignage ${i + 1}`}
                onClick={() => {
                  scrollTo(i);
                  pause();
                  setTimeout(resume, 6000);
                }}
                className={`k-testi-dot ${active === i ? 'is-active' : ''} ${paused ? 'is-paused' : 'is-playing'}`}
                style={{ ['--dot-color' as any]: q.c }}
              >
                <span className="k-testi-dot__fill" />
              </button>
            ))}
          </div>
          <div className="k-testi-hint">
            <span className="k-testi-hint__dot" />
            <span>{paused ? 'En pause — touchez pour reprendre' : 'Défilement auto — swipe pour naviguer'}</span>
          </div>
        </div>
      </div>
    </section>
  );
};

export default TestimonialsSection;
